import React, { useEffect, useMemo, useState } from "react";
import { Box, Flex, Text, Textarea, useToast } from "@chakra-ui/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AppBar } from "../../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../../components/desktopUi";
import { CenterSpinner, M, PropCell } from "../../../components/managementUi";
import { getMyKpiDetailApi } from "../../../api/evaluation.api";
import { updateLeaderCommentApi } from "../../../api/statisticalReport.api";

// Khớp app: app/(feature)/management/statistical-report/add-evaluation.tsx
const Field = ({ label, required, placeholder, value, onChange, error }) => (
  <Box mb="16px">
    <Flex align="center">
      <Text fontSize="14px" fontWeight={500} color={M.baseContent}>
        {label}
      </Text>
      {required ? (
        <Text fontSize="14px" color={M.red}>
          {" *"}
        </Text>
      ) : null}
    </Flex>
    <Box h="6px" />
    <Textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      minH="96px"
      bg={M.field}
      border="1px solid"
      borderColor={error ? M.red : M.border}
      borderRadius="8px"
      fontSize="14px"
      _placeholder={{ color: M.grey200 }}
    />
    {error ? (
      <Text fontSize="12px" color={M.red} mt="4px">
        {error}
      </Text>
    ) : null}
  </Box>
);

const AddEvaluation = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const toast = useToast();
  const { code } = useParams();
  const [search] = useSearchParams();
  const month = Number(search.get("month")) || new Date().getMonth() + 1;
  const year = Number(search.get("year")) || new Date().getFullYear();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState("");
  const [solution, setSolution] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!code) return;
    setLoading(true);
    getMyKpiDetailApi({ month, year, code })
      .then((d) => setDetail(d || null))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [code, month, year]);

  const statusLabel = useMemo(() => {
    const cls = detail?.evaluation?.classification;
    if (cls?.code === "DAT" && Number(detail?.evaluation?.metrics?.consecutiveMonthsWithoutTransactions || 0) >= 2) {
      return `${cls?.label || "-"} (*)`;
    }
    return cls?.label || "-";
  }, [detail]);

  const onSubmit = async () => {
    const next = {};
    if (!reason.trim()) next.reason = "Vui lòng nhập lý do";
    if (!solution.trim()) next.solution = "Vui lòng nhập giải pháp";
    setErrors(next);
    if (Object.keys(next).length) return;

    const existing = detail?.evaluation?.leaderComment?.items ?? [];
    const items = [
      ...existing.map(({ reason: r, solution: s }) => ({ reason: r ?? "", solution: s ?? "" })),
      { reason: reason.trim(), solution: solution.trim() },
    ];

    setSubmitting(true);
    try {
      await updateLeaderCommentApi({
        code,
        month,
        year,
        leaderComment: { items },
      });
      toast({ title: "Thêm đánh giá thành công", status: "success", duration: 2500, position: "top" });
      navigate(-1);
    } catch (e) {
      toast({ title: e?.message || "Thêm đánh giá thất bại", status: "error", duration: 3000, position: "top" });
    } finally {
      setSubmitting(false);
    }
  };

  const pageBody = (
    <>

      {loading ? (
        <CenterSpinner />
      ) : (
        <Box px="16px" pt="12px">
          {/* Thông tin KPI */}
          <Box border="1px solid" borderColor={M.border} borderRadius="12px" bg="white" overflow="hidden">
            <Box px="16px" py="12px">
              <Text fontSize="14px" fontWeight={600} color={M.baseContent}>
                Thông tin KPI
              </Text>
            </Box>
            <Box px="16px">
              <PropCell label="Đánh giá" value={statusLabel} />
              <PropCell
                label="Tháng"
                value={
                  detail?.period?.month && detail?.period?.year
                    ? `${detail.period.month}/${detail.period.year}`
                    : `${month}/${year}`
                }
              />
              <PropCell label="Tên nhân viên" value={detail?.employee?.fullName || "-"} />
              <PropCell label="Mã nhân viên" value={detail?.employee?.code || code || "-"} />
              <PropCell label="Số cuộc tư vấn" value={String(detail?.evaluation?.metrics?.consultCount ?? "-")} />
              <PropCell label="Số leads" value={String(detail?.evaluation?.metrics?.leadCount ?? "-")} />
              <PropCell label="Số giao dịch" value={String(detail?.evaluation?.metrics?.transactionCount ?? "-")} />
              <PropCell label="Ghi chú" value={detail?.evaluation?.note || "-"} borderBottom={false} />
            </Box>
          </Box>

          <Box h="12px" />

          {/* Nội dung đánh giá */}
          <Box border="1px solid" borderColor={M.border} borderRadius="12px" bg="white" overflow="hidden">
            <Box px="16px" py="12px">
              <Text fontSize="14px" fontWeight={600} color={M.baseContent}>
                Nội dung đánh giá
              </Text>
            </Box>
            <Box px="16px" pb="4px">
              <Field
                label="Lý do"
                required
                placeholder="Nhập lý do..."
                value={reason}
                onChange={setReason}
                error={errors.reason}
              />
              <Field
                label="Giải pháp"
                required
                placeholder="Nhập giải pháp..."
                value={solution}
                onChange={setSolution}
                error={errors.solution}
              />
            </Box>
          </Box>
        </Box>
      )}

      <Box
        position={{ base: "fixed", md: "static" }}
        bottom={{ base: "0", md: "auto" }}
        left={{ base: "50%", md: "auto" }}
        transform={{ base: "translateX(-50%)", md: "none" }}
        w="100%"
        maxW={{ base: "480px", md: "100%" }}
        bg="white"
        borderTop="1px solid"
        borderColor={M.border}
        px="16px"
        py="12px"
        zIndex={20}
      >
        <Flex gap="12px">
          <Flex
            as="button"
            type="button"
            flex="1"
            h="48px"
            align="center"
            justify="center"
            borderRadius="8px"
            border="1px solid"
            borderColor={M.primary}
            onClick={() => navigate(-1)}
          >
            <Text fontSize="16px" fontWeight={600} color={M.primary}>
              Hủy
            </Text>
          </Flex>
          <Flex
            as="button"
            type="button"
            flex="1"
            h="48px"
            align="center"
            justify="center"
            borderRadius="8px"
            bg={M.primary}
            opacity={submitting ? 0.6 : 1}
            onClick={() => !submitting && onSubmit()}
          >
            <Text fontSize="16px" fontWeight={700} color="white">
              Thêm đánh giá
            </Text>
          </Flex>
        </Flex>
      </Box>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Thêm đánh giá" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="100px">
      <AppBar title="Thêm đánh giá" />
      {pageBody}
    </Box>
  );
};

export default AddEvaluation;
